import { vaultKindName, type VaultLinkKind } from '@hive/shared';
import { File as FileIcon, Film, FileText, Folder, Globe, PenTool, Play, Presentation, Sheet } from 'lucide-react';
import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { useSignedFileUrl, type HiveFileRow, type VaultLinkRow } from '@/data/vault';
import { desktopBridge } from '@/lib/bridge';
import { useLinkThumbnails } from './useLinkThumbnails';
import { drivePreviewUrl } from '@/lib/drive';
import { formatBytes, linkTitle } from './model';
import { cn } from '@/lib/utils';

const KIND_ICONS: Record<VaultLinkKind, typeof FileIcon> = {
  drive_doc: FileText,
  drive_sheet: Sheet,
  drive_slides: Presentation,
  drive_folder: Folder,
  drive_video: Film,
  drive_file: FileIcon,
  figma: PenTool,
  web: Globe,
};

function site(link: VaultLinkRow): string {
  const p = link.preview as { site?: string | null } | null;
  if (p?.site) return p.site;
  try {
    return new URL(link.url).hostname.replace(/^www\./, '');
  } catch {
    return '';
  }
}

/** The first of `srcs` that loads, or the kind's icon. */
export function Thumb({ srcs, kind, className }: { srcs: readonly string[]; kind: VaultLinkKind; className?: string }) {
  const [failed, setFailed] = useState<ReadonlySet<string>>(new Set());
  const src = srcs.find((s) => !failed.has(s));
  const Icon = KIND_ICONS[kind];
  return (
    <div className={cn('grid aspect-video place-items-center overflow-hidden bg-bg-3', className)}>
      {src ? (
        <img
          key={src}
          src={src}
          alt=""
          loading="lazy"
          referrerPolicy="no-referrer"
          className="size-full object-cover"
          onError={() => setFailed((f) => new Set(f).add(src))}
        />
      ) : (
        <Icon className="size-8 text-dim" aria-hidden />
      )}
    </div>
  );
}

export function VaultLinkCard({ link, onOpen }: { link: VaultLinkRow; onOpen: () => void }) {
  const thumbs = useLinkThumbnails(link);
  return (
    <button
      type="button"
      onClick={onOpen}
      className="group flex w-full cursor-pointer flex-col bg-bg-2/85 text-left shadow-[inset_0_0_0_1px_var(--color-line)] hover:shadow-[inset_0_0_0_1px_var(--color-cyan)]"
    >
      <Thumb srcs={thumbs} kind={link.kind} />
      <span className="flex flex-col gap-0.5 p-2.5">
        <span className="line-clamp-2 text-sm font-semibold break-words group-hover:text-cyan">{linkTitle(link)}</span>
        <span className="font-mono text-[11px] text-muted">
          {vaultKindName(link.kind)} · {site(link)}
        </span>
      </span>
    </button>
  );
}

export function FileCard({ file, onOpen }: { file: HiveFileRow; onOpen: () => void }) {
  const isImage = file.mime_type.startsWith('image/');
  const url = useSignedFileUrl(isImage ? file.storage_path : null);
  return (
    <button
      type="button"
      onClick={onOpen}
      className="group flex w-full cursor-pointer flex-col bg-bg-2/85 text-left shadow-[inset_0_0_0_1px_var(--color-line)] hover:shadow-[inset_0_0_0_1px_var(--color-cyan)]"
    >
      <div className="grid aspect-video place-items-center overflow-hidden bg-bg-3">
        {isImage && url.data ? (
          <img src={url.data} alt="" loading="lazy" className="size-full object-cover" />
        ) : (
          <FileIcon className="size-8 text-dim" aria-hidden />
        )}
      </div>
      <span className="flex flex-col gap-0.5 p-2.5">
        <span className="line-clamp-2 text-sm font-semibold break-words group-hover:text-cyan">{file.name}</span>
        <span className="font-mono text-[11px] text-muted">In Hive · {formatBytes(file.size_bytes)}</span>
      </span>
    </button>
  );
}

/** The big preview on a link's detail: Drive's player for video, the thumbnail otherwise. */
export function LinkPreview({ link }: { link: VaultLinkRow }) {
  const thumbs = useLinkThumbnails(link);
  const bridge = desktopBridge();
  if (link.kind === 'drive_video' && link.provider_id && bridge) {
    // Drive's player needs a Google sign-in, which the desktop window can't have.
    return (
      <div className="flex flex-col gap-2">
        <Thumb srcs={thumbs} kind={link.kind} className="max-h-72" />
        <div className="flex flex-wrap items-center gap-3">
          <Button size="sm" onClick={() => void bridge.openExternal(link.url)}>
            <Play aria-hidden /> Play in Drive
          </Button>
          <p className="font-mono text-[11px] text-muted">
            Opens in your browser. New videos take Drive a few minutes to process.
          </p>
        </div>
      </div>
    );
  }
  if (link.kind === 'drive_video' && link.provider_id) {
    return (
      <div className="flex flex-col gap-1">
        <iframe
          src={drivePreviewUrl(link.provider_id)}
          title={linkTitle(link)}
          allow="autoplay; fullscreen"
          allowFullScreen
          className="aspect-video w-full border-0 bg-black"
        />
        <p className="font-mono text-[11px] text-muted">
          Just uploaded? Drive takes a few minutes to process a new video before it plays here.
        </p>
      </div>
    );
  }
  const description = (link.preview as { description?: string | null } | null)?.description;
  return (
    <div className="flex flex-col gap-2">
      <Thumb srcs={thumbs} kind={link.kind} className="max-h-72" />
      {description ? <p className="text-sm text-muted">{description}</p> : null}
    </div>
  );
}

/** A Hive file inline: an image shows itself, anything else is a chip with a download link. */
export function FileView({ file, small = false }: { file: HiveFileRow; small?: boolean }) {
  const url = useSignedFileUrl(file.storage_path);
  if (file.mime_type.startsWith('image/')) {
    return url.data ? (
      <a href={url.data} target="_blank" rel="noreferrer" className="block">
        <img
          src={url.data}
          alt={file.name}
          className={cn('bg-bg-3 object-contain', small ? 'max-h-40 max-w-full' : 'max-h-96 w-full')}
        />
      </a>
    ) : (
      <div className="aspect-video w-full bg-bg-3" />
    );
  }
  return <FileChip file={file} href={url.data ?? null} />;
}

export function FileChip({ file, href }: { file: HiveFileRow; href: string | null }) {
  return (
    <a
      href={href ?? undefined}
      download={file.name}
      target="_blank"
      rel="noreferrer"
      className="inline-flex max-w-full items-center gap-2 bg-bg-3 px-2.5 py-1.5 font-mono text-xs text-text shadow-[inset_0_0_0_1px_var(--color-line)] hover:text-cyan"
    >
      <FileIcon className="size-3.5 shrink-0" aria-hidden />
      <span className="truncate">{file.name}</span>
      <span className="text-muted">{formatBytes(file.size_bytes)}</span>
    </a>
  );
}
